import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Navigation } from '../components/Navigation.tsx';
import {
  MARKETING_SKILLS,
  MarketingAnalyticsSnapshot,
  MarketingAppId,
  MarketingAppProfile,
  MarketingPostedVideo,
  MarketingRunDecision,
  MarketingSkillRun,
  MarketingSyncRun,
  MarketingVideoPlatform,
  addMarketingPostedVideoSnapshot,
  createMarketingPostedVideo,
  getLatestMarketingAnalyticsSnapshot,
  getMarketingPostedVideos,
  getMarketingProfile,
  getMarketingSkillRuns,
  getRecentMarketingSyncRuns,
  requestMarketingAnalyticsSync,
  requestMarketingSync,
  runMarketingSkill,
  seedMarketingProfiles,
  updateMarketingSkillDecision,
} from '../services/marketingCockpitService.ts';
import {
  ViralTrendInput,
  WeeklyMarketingAction,
  assessPostedVideoTraction,
  buildWeeklyMarketingActions,
  buildViralTrendInputs,
  summarizeActivation,
} from '../services/marketingActionPlanner.ts';
import './MarketingDashboard.css';

const APP_IDS: MarketingAppId[] = ['spontane', 'emulsify'];
type MarketingAppSection = 'actions' | 'evidence' | 'memory';

const APP_SECTIONS: Array<{ id: MarketingAppSection; label: string; description: string }> = [
  { id: 'actions', label: 'This Week', description: 'The few tests and actions that should happen next.' },
  { id: 'evidence', label: 'Evidence', description: 'Brand memory, trend sync, and analytics inputs.' },
  { id: 'memory', label: 'Memory', description: 'Recommendation history and supporting skill runs.' },
];

function formatDate(iso?: string): string {
  if (!iso) return 'Never';
  try {
    return new Date(iso).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' });
  } catch {
    return iso;
  }
}

function appAccent(appId: MarketingAppId): string {
  return appId === 'spontane' ? 'blue' : 'orange';
}

function latestCompletedSync(runs: MarketingSyncRun[]): MarketingSyncRun | null {
  return runs.find((run) => run.status === 'completed') || null;
}

function latestSyncLabel(runs: MarketingSyncRun[]): string {
  const latest = latestCompletedSync(runs);
  return latest ? formatDate(latest.requestedAt) : 'No completed sync yet';
}

function sectionPath(appId: MarketingAppId, section: MarketingAppSection): string {
  return section === 'actions'
    ? `/admin/marketing/${appId}`
    : `/admin/marketing/${appId}/${section}`;
}

const MarketingSubnav: React.FC<{ active?: MarketingAppId | 'overview' }> = ({ active = 'overview' }) => (
  <div className="marketing-subnav">
    <Link className={`marketing-subnav-link ${active === 'overview' ? 'active' : ''}`} to="/admin/marketing">
      Overview
    </Link>
    <Link className={`marketing-subnav-link ${active === 'spontane' ? 'active' : ''}`} to="/admin/marketing/spontane">
      Spontane
    </Link>
    <Link className={`marketing-subnav-link ${active === 'emulsify' ? 'active' : ''}`} to="/admin/marketing/emulsify">
      Emulsify
    </Link>
  </div>
);

const MarketingAppSectionNav: React.FC<{ appId: MarketingAppId; active: MarketingAppSection }> = ({ appId, active }) => (
  <div className="marketing-section-nav" aria-label="Marketing app sections">
    {APP_SECTIONS.map((section) => (
      <Link
        key={section.id}
        to={sectionPath(appId, section.id)}
        className={`marketing-section-link ${active === section.id ? 'active' : ''}`}
      >
        <strong>{section.label}</strong>
        <span>{section.description}</span>
      </Link>
    ))}
  </div>
);

const StatusPill: React.FC<{ status: string }> = ({ status }) => (
  <span className={`marketing-status marketing-status--${status.replace(/_/g, '-')}`}>{status.replace(/_/g, ' ')}</span>
);

const LatestDataCard: React.FC<{ runs: MarketingSyncRun[]; syncing: boolean }> = ({ runs, syncing }) => {
  const latest = latestCompletedSync(runs);
  const sourceStatuses = latest?.sourceDetails?.length
    ? latest.sourceDetails.map((source) => `${source.name}: ${source.status}`)
    : latest?.sources || [];

  return (
    <section className="marketing-card marketing-latest-data">
      <div className="marketing-card-header">
        <div>
          <h3>Latest Data Used</h3>
          <p className="marketing-muted">Recommendations and skill runs use the latest completed sync below.</p>
        </div>
        <StatusPill status={syncing ? 'running' : latest ? latest.status : 'needs_setup'} />
      </div>
      <div className="marketing-data-strip">
        <div>
          <span className="marketing-label">Last completed sync</span>
          <strong>{latest ? formatDate(latest.requestedAt) : 'None yet'}</strong>
        </div>
        <div>
          <span className="marketing-label">Sources</span>
          <p>{sourceStatuses.join(' + ') || 'Run sync to collect source data.'}</p>
        </div>
      </div>
      {syncing && <div className="marketing-progress"><span /> Sync in progress. Keep this page open or refresh later.</div>}
    </section>
  );
};

const SourceCard: React.FC<{ title: string; source: MarketingAppProfile['virlo'] }> = ({ title, source }) => (
  <section className="marketing-card marketing-source-card">
    <div className="marketing-card-header">
      <h3>{title}</h3>
      <StatusPill status={source.status} />
    </div>
    <p className="marketing-muted">{source.note}</p>
    <div className="marketing-card-footer">
      <span>{source.trackedNiches.length} niches</span>
      <span>{source.trackedCompetitors.length} competitors</span>
      <span>{source.trackedKeywords.length} keywords</span>
    </div>
    <div className="marketing-source-grid">
      <div>
        <span className="marketing-label">Niches</span>
        <p>{source.trackedNiches.join(', ') || 'None yet'}</p>
      </div>
      <div>
        <span className="marketing-label">Creators</span>
        <p>{source.trackedCreators.join(', ') || 'None yet'}</p>
      </div>
      <div>
        <span className="marketing-label">Competitors</span>
        <p>{source.trackedCompetitors.join(', ') || 'None yet'}</p>
      </div>
      <div>
        <span className="marketing-label">Keywords</span>
        <p>{source.trackedKeywords.join(', ') || 'None yet'}</p>
      </div>
    </div>
  </section>
);

const ScopeFreezeNotice: React.FC = () => (
  <section className="marketing-freeze">
    <div>
      <span className="marketing-eyebrow-dark">Scope freeze</span>
      <strong>Use the cockpit to choose tests, not to avoid market contact.</strong>
      <p>
        Keep brand memory, Virlo/DataForSEO, GA4/Firebase analytics, and weekly recommendations. Pause new ad
        spend sync, budget automation, advanced dashboards, and extra agents.
      </p>
    </div>
  </section>
);

const WeeklyActionCard: React.FC<{ action: WeeklyMarketingAction; index: number }> = ({ action, index }) => (
  <article className="marketing-weekly-action">
    <div className="marketing-action-rank">{index + 1}</div>
    <div className="marketing-weekly-action-body">
      <div className="marketing-card-header">
        <div>
          <span className="marketing-channel">{action.source}</span>
          <h3>{action.creative}</h3>
        </div>
        <StatusPill status={action.status} />
      </div>
      <p className="marketing-hypothesis">{action.hypothesis}</p>
      <div className="marketing-action-brief">
        <div>
          <span className="marketing-label">Action</span>
          <p>{action.action}</p>
        </div>
        <div>
          <span className="marketing-label">Metric</span>
          <p>{action.metric}</p>
        </div>
        <div>
          <span className="marketing-label">Decision rule</span>
          <p>{action.decisionRule}</p>
        </div>
      </div>
      {action.evidence.length > 0 && (
        <div className="marketing-evidence-line">
          {action.evidence.slice(0, 2).map((item) => (
            <span key={item}>{item}</span>
          ))}
        </div>
      )}
    </div>
  </article>
);

const WeeklyActionsPanel: React.FC<{
  profile: MarketingAppProfile;
  runs: MarketingSkillRun[];
  analytics: MarketingAnalyticsSnapshot | null;
  trends?: ViralTrendInput[];
  compact?: boolean;
}> = ({ profile, runs, analytics, trends = [], compact = false }) => {
  const actions = buildWeeklyMarketingActions(profile, runs, analytics, compact ? 2 : 3, trends);

  return (
    <section className="marketing-card marketing-weekly-panel">
      <div className="marketing-card-header">
        <div>
          <h3>{compact ? `${profile.name}: Next Tests` : 'This Week: Tests & Actions'}</h3>
          <p className="marketing-muted">
            {summarizeActivation(analytics)}. Pick small tests, execute them manually, then mark the learning in memory.
          </p>
        </div>
        <span className="marketing-count">{actions.length} tests</span>
      </div>
      <div className="marketing-weekly-list">
        {actions.map((action, index) => (
          <WeeklyActionCard key={action.id} action={action} index={index} />
        ))}
      </div>
    </section>
  );
};

const TrendInputsPanel: React.FC<{ trends: ViralTrendInput[]; onSync?: () => void; syncing?: boolean }> = ({
  trends,
  onSync,
  syncing,
}) => (
  <section className="marketing-card marketing-trend-panel">
    <div className="marketing-card-header">
      <div>
        <h3>Relevant Viral Inputs</h3>
        <p className="marketing-muted">Filtered audio and hashtags from Virlo. News, politics, and generic viral noise are excluded.</p>
      </div>
      {onSync && (
        <button type="button" onClick={onSync} disabled={syncing}>
          {syncing ? 'Syncing...' : 'Sync trends'}
        </button>
      )}
    </div>
    {trends.length === 0 ? (
      <p className="marketing-empty">No relevant reusable audio or hashtags found in the latest sync. Broaden Virlo tracking or sync again.</p>
    ) : (
      <div className="marketing-trend-grid">
        {trends.map((trend) => (
          <article key={trend.id} className="marketing-trend-card">
            <div className="marketing-card-header">
              <span className="marketing-channel">{trend.platform}</span>
              <strong>{trend.views ? `${trend.views.toLocaleString()} views` : 'View count unknown'}</strong>
            </div>
            <h4>{trend.audio || trend.hashtags.join(' ')}</h4>
            <p className="marketing-muted">{trend.title}</p>
            {trend.audio && (
              <p>
                <span className="marketing-label">Audio</span>
                {trend.audio}
              </p>
            )}
            {trend.hashtags.length > 0 && (
              <div className="marketing-pill-list compact">
                {trend.hashtags.map((tag) => (
                  <span key={tag}>{tag.startsWith('#') ? tag : `#${tag}`}</span>
                ))}
              </div>
            )}
            <small>{trend.whyItMatters}</small>
            {trend.url && <a href={trend.url} target="_blank" rel="noreferrer">Open example</a>}
          </article>
        ))}
      </div>
    )}
  </section>
);

function parseHashtags(value: string): string[] {
  return value
    .split(/[,\s]+/)
    .map((item) => item.trim())
    .filter(Boolean)
    .map((item) => (item.startsWith('#') ? item : `#${item}`));
}

const emptyVideoForm = {
  platform: 'TikTok' as MarketingVideoPlatform,
  url: '',
  title: '',
  campaign: '',
  audio: '',
  hashtags: '',
  views: '',
  likes: '',
  comments: '',
  shares: '',
  saves: '',
};

const PostedVideosPanel: React.FC<{
  videos: MarketingPostedVideo[];
  onCreate: (input: typeof emptyVideoForm) => Promise<void>;
  onSnapshot: (video: MarketingPostedVideo, snapshot: typeof emptyVideoForm) => Promise<void>;
}> = ({ videos, onCreate, onSnapshot }) => {
  const [form, setForm] = useState(emptyVideoForm);
  const [snapshotForms, setSnapshotForms] = useState<Record<string, typeof emptyVideoForm>>({});
  const [saving, setSaving] = useState(false);

  const updateForm = (field: keyof typeof emptyVideoForm, value: string) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const updateSnapshotForm = (videoId: string, field: keyof typeof emptyVideoForm, value: string) => {
    setSnapshotForms((prev) => ({
      ...prev,
      [videoId]: { ...(prev[videoId] || emptyVideoForm), [field]: value },
    }));
  };

  const submitVideo = async (event: React.FormEvent) => {
    event.preventDefault();
    setSaving(true);
    try {
      await onCreate(form);
      setForm(emptyVideoForm);
    } finally {
      setSaving(false);
    }
  };

  const submitSnapshot = async (event: React.FormEvent, video: MarketingPostedVideo) => {
    event.preventDefault();
    const snapshot = snapshotForms[video.id] || emptyVideoForm;
    await onSnapshot(video, snapshot);
    setSnapshotForms((prev) => ({ ...prev, [video.id]: emptyVideoForm }));
  };

  return (
    <section className="marketing-card marketing-posted-panel">
      <div className="marketing-card-header">
        <div>
          <h3>Posted Videos & Traction</h3>
          <p className="marketing-muted">Paste each post after publishing, then add snapshots to see if it is moving.</p>
        </div>
        <span className="marketing-count">{videos.length} posts</span>
      </div>

      <form className="marketing-post-form" onSubmit={submitVideo}>
        <select value={form.platform} onChange={(event) => updateForm('platform', event.target.value)}>
          {(['TikTok', 'Instagram Reels', 'YouTube Shorts', 'Other'] as MarketingVideoPlatform[]).map((platform) => (
            <option key={platform} value={platform}>{platform}</option>
          ))}
        </select>
        <input placeholder="Post URL" value={form.url} onChange={(event) => updateForm('url', event.target.value)} required />
        <input placeholder="Test title / hook" value={form.title} onChange={(event) => updateForm('title', event.target.value)} required />
        <input placeholder="Campaign / UTM name" value={form.campaign} onChange={(event) => updateForm('campaign', event.target.value)} />
        <input placeholder="Audio used" value={form.audio} onChange={(event) => updateForm('audio', event.target.value)} />
        <input placeholder="#hashtags" value={form.hashtags} onChange={(event) => updateForm('hashtags', event.target.value)} />
        <input type="number" min="0" placeholder="Views now" value={form.views} onChange={(event) => updateForm('views', event.target.value)} />
        <input type="number" min="0" placeholder="Likes" value={form.likes} onChange={(event) => updateForm('likes', event.target.value)} />
        <input type="number" min="0" placeholder="Comments" value={form.comments} onChange={(event) => updateForm('comments', event.target.value)} />
        <input type="number" min="0" placeholder="Shares" value={form.shares} onChange={(event) => updateForm('shares', event.target.value)} />
        <input type="number" min="0" placeholder="Saves" value={form.saves} onChange={(event) => updateForm('saves', event.target.value)} />
        <button type="submit" className="marketing-primary" disabled={saving}>{saving ? 'Saving...' : 'Track post'}</button>
      </form>

      {videos.length === 0 ? (
        <p className="marketing-empty">No posted videos tracked yet.</p>
      ) : (
        <div className="marketing-post-list">
          {videos.map((video) => {
            const traction = assessPostedVideoTraction(video);
            const snapshotForm = snapshotForms[video.id] || emptyVideoForm;
            return (
              <article key={video.id} className="marketing-post-card">
                <div className="marketing-card-header">
                  <div>
                    <span className="marketing-channel">{video.platform}</span>
                    <h4>{video.title}</h4>
                  </div>
                  <StatusPill status={traction.verdict} />
                </div>
                <p>{traction.summary}</p>
                <div className="marketing-evidence-line">
                  {video.audio && <span>Audio: {video.audio}</span>}
                  {video.hashtags.slice(0, 4).map((tag) => <span key={tag}>{tag}</span>)}
                  {video.campaign && <span>Campaign: {video.campaign}</span>}
                </div>
                <a href={video.url} target="_blank" rel="noreferrer">Open post</a>
                <form className="marketing-snapshot-form" onSubmit={(event) => submitSnapshot(event, video)}>
                  <input type="number" min="0" placeholder="Views" value={snapshotForm.views} onChange={(event) => updateSnapshotForm(video.id, 'views', event.target.value)} required />
                  <input type="number" min="0" placeholder="Likes" value={snapshotForm.likes} onChange={(event) => updateSnapshotForm(video.id, 'likes', event.target.value)} />
                  <input type="number" min="0" placeholder="Comments" value={snapshotForm.comments} onChange={(event) => updateSnapshotForm(video.id, 'comments', event.target.value)} />
                  <input type="number" min="0" placeholder="Shares" value={snapshotForm.shares} onChange={(event) => updateSnapshotForm(video.id, 'shares', event.target.value)} />
                  <input type="number" min="0" placeholder="Saves" value={snapshotForm.saves} onChange={(event) => updateSnapshotForm(video.id, 'saves', event.target.value)} />
                  <button type="submit">Add snapshot</button>
                </form>
              </article>
            );
          })}
        </div>
      )}
    </section>
  );
};

const SyncRuns: React.FC<{ runs: MarketingSyncRun[] }> = ({ runs }) => (
  <section className="marketing-card">
    <div className="marketing-card-header">
      <h3>Recent Syncs</h3>
    </div>
    {runs.length === 0 ? (
      <p className="marketing-empty">No sync requests yet.</p>
    ) : (
      <div className="marketing-list">
        {runs.map((run) => (
          <div key={run.id} className="marketing-list-row">
            <div>
              <strong>{run.appId}</strong>
              <p>{run.summary}</p>
              <small>{run.sources.join(' + ')}</small>
            </div>
            <div className="marketing-list-meta">
              <StatusPill status={run.status} />
              <small>{formatDate(run.requestedAt)}</small>
            </div>
          </div>
        ))}
      </div>
    )}
  </section>
);

const AnalyticsSnapshotCard: React.FC<{
  snapshot: MarketingAnalyticsSnapshot | null;
  syncing: boolean;
  onSync: () => void;
}> = ({ snapshot, syncing, onSync }) => {
  const totals = snapshot?.totals;
  const campaigns = snapshot?.campaigns || [];
  const events = snapshot?.events || [];

  return (
    <section className="marketing-card marketing-analytics-card">
      <div className="marketing-card-header">
        <div>
          <h3>App Analytics & Campaign Measurement</h3>
          <p className="marketing-muted">
            Imports GA4/Firebase Analytics events and relates campaign/source rows to installs and activation.
          </p>
        </div>
        <div className="marketing-list-meta">
          <StatusPill status={syncing ? 'running' : snapshot?.status || 'needs_setup'} />
          <button type="button" onClick={onSync} disabled={syncing}>
            {syncing ? 'Syncing...' : 'Sync analytics'}
          </button>
        </div>
      </div>

      {!snapshot ? (
        <p className="marketing-empty">No analytics snapshot yet. Sync analytics after GA4 property IDs are configured.</p>
      ) : (
        <>
          <p className="marketing-muted">{snapshot.summary}</p>
          {snapshot.setupHint && <div className="marketing-banner">{snapshot.setupHint}</div>}
          <div className="marketing-analytics-kpis">
            <div>
              <span className="marketing-label">Installs</span>
              <strong>{totals?.installs ?? 0}</strong>
            </div>
            <div>
              <span className="marketing-label">App opens</span>
              <strong>{totals?.appOpens ?? 0}</strong>
            </div>
            <div>
              <span className="marketing-label">Activations</span>
              <strong>{totals?.activations ?? 0}</strong>
            </div>
            <div>
              <span className="marketing-label">Activation rate</span>
              <strong>{totals?.activationRate ?? 0}%</strong>
            </div>
          </div>

          <div className="marketing-two-column">
            <div>
              <h4>Core Events</h4>
              {events.length ? (
                <div className="marketing-mini-table">
                  {events.slice(0, 8).map((event) => (
                    <div key={event.eventName}>
                      <span>{event.eventName}</span>
                      <strong>{event.eventCount}</strong>
                      <small>{event.activeUsers} users</small>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="marketing-empty">No core events returned yet.</p>
              )}
            </div>
            <div>
              <h4>Campaign Rows</h4>
              {campaigns.length ? (
                <div className="marketing-mini-table">
                  {campaigns.slice(0, 8).map((campaign) => (
                    <div key={`${campaign.sourceMedium}-${campaign.campaignName}`}>
                      <span>{campaign.campaignName || '(not set)'}</span>
                      <strong>{campaign.activations} activations</strong>
                      <small>
                        {campaign.sourceMedium} | {campaign.installs} installs | {campaign.activationRate}%
                      </small>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="marketing-empty">No campaign-attributed rows yet. Use UTM/campaign parameters in ads.</p>
              )}
            </div>
          </div>
          <p className="marketing-muted">
            Latest analytics sync: {formatDate(snapshot.syncedAt)} | Range: {snapshot.dateRange.start} to {snapshot.dateRange.end}
          </p>
        </>
      )}
    </section>
  );
};

const SkillRuns: React.FC<{
  runs: MarketingSkillRun[];
  onDecision: (run: MarketingSkillRun, decision: MarketingRunDecision) => void;
}> = ({ runs, onDecision }) => (
  <section className="marketing-card">
    <div className="marketing-card-header">
      <h3>Marketing Memory</h3>
      <span className="marketing-count">{runs.length} runs</span>
    </div>
    {runs.length === 0 ? (
      <p className="marketing-empty">No skill runs yet. Run a skill to start building marketing memory.</p>
    ) : (
      <div className="marketing-skill-run-list">
        {runs.map((run) => (
          <article key={run.id} className="marketing-skill-run">
            <div className="marketing-card-header">
              <div>
                <h4>{run.skillLabel}</h4>
                <small>{formatDate(run.createdAt)} | Confidence: {run.confidence}</small>
              </div>
              <StatusPill status={run.decision} />
            </div>
            <p className="marketing-objective">{run.objective}</p>
            <p>{run.recommendation}</p>
            <ul>
              {run.nextActions.map((action) => (
                <li key={action}>{action}</li>
              ))}
            </ul>
            <p className="marketing-muted">Measurement target: {run.measurementTarget}</p>
            <div className="marketing-decision-row">
              {(['accepted', 'rejected', 'needs_revision', 'executed'] as MarketingRunDecision[]).map((decision) => (
                <button key={decision} type="button" onClick={() => onDecision(run, decision)}>
                  {decision.replace('_', ' ')}
                </button>
              ))}
            </div>
          </article>
        ))}
      </div>
    )}
  </section>
);

export const MarketingDashboard: React.FC = () => {
  const [profiles, setProfiles] = useState<MarketingAppProfile[]>([]);
  const [syncRuns, setSyncRuns] = useState<MarketingSyncRun[]>([]);
  const [skillRunsByApp, setSkillRunsByApp] = useState<Record<MarketingAppId, MarketingSkillRun[]>>({
    spontane: [],
    emulsify: [],
  });
  const [analyticsByApp, setAnalyticsByApp] = useState<Record<MarketingAppId, MarketingAnalyticsSnapshot | null>>({
    spontane: null,
    emulsify: null,
  });
  const [loading, setLoading] = useState(true);
  const [syncingAll, setSyncingAll] = useState(false);
  const [message, setMessage] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    const loadedProfiles = await Promise.all(APP_IDS.map((appId) => getMarketingProfile(appId)));
    const loadedRuns = await getRecentMarketingSyncRuns();
    const loadedSkillRuns = await Promise.all(APP_IDS.map((appId) => getMarketingSkillRuns(appId)));
    const loadedAnalytics = await Promise.all(APP_IDS.map((appId) => getLatestMarketingAnalyticsSnapshot(appId)));
    setProfiles(loadedProfiles);
    setSyncRuns(loadedRuns);
    setSkillRunsByApp({
      spontane: loadedSkillRuns[0],
      emulsify: loadedSkillRuns[1],
    });
    setAnalyticsByApp({
      spontane: loadedAnalytics[0],
      emulsify: loadedAnalytics[1],
    });
    setLoading(false);
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const seedProfiles = async () => {
    setMessage('Seeding default marketing profiles...');
    await seedMarketingProfiles();
    await load();
    setMessage('Marketing profiles seeded.');
  };

  const syncAll = async () => {
    setSyncingAll(true);
    setMessage('Syncing Virlo and DataForSEO for both apps...');
    try {
      const run = await requestMarketingSync('all');
      await load();
      setSyncRuns((prev) => [run, ...prev.filter((item) => item.id !== run.id)]);
      setMessage(run.status === 'completed' ? 'All-app sync completed.' : 'All-app sync saved.');
    } finally {
      setSyncingAll(false);
    }
  };

  return (
    <div className="marketing-dashboard">
      <Navigation />
      <main className="marketing-shell">
        <header className="marketing-hero">
          <div>
            <span className="marketing-eyebrow">CVL AI Marketing Cockpit</span>
            <h1>Weekly Marketing Actions</h1>
            <p>
              A lightweight board for deciding what to test next across Spontane and Emulsify. The goal is execution,
              measurement, and learning, not more preparation.
            </p>
          </div>
          <div className="marketing-actions">
            <button type="button" onClick={seedProfiles} disabled={loading}>
              Seed defaults
            </button>
            <button type="button" className="marketing-primary" onClick={syncAll} disabled={syncingAll}>
              {syncingAll ? 'Syncing...' : 'Sync evidence'}
            </button>
          </div>
        </header>

        <MarketingSubnav active="overview" />
        {message && <div className="marketing-banner">{message}</div>}
        <ScopeFreezeNotice />

        <section className="marketing-overview-actions">
          {profiles.map((profile) => (
            <div key={profile.id} className={`marketing-app-action-column ${appAccent(profile.id)}`}>
              <div className="marketing-app-action-header">
                <div>
                  <span className="marketing-label">{profile.market}</span>
                  <h2>{profile.name}</h2>
                </div>
                <Link to={`/admin/marketing/${profile.id}`}>Open</Link>
              </div>
              <WeeklyActionsPanel
                profile={profile}
                runs={skillRunsByApp[profile.id]}
                analytics={analyticsByApp[profile.id]}
                compact
              />
            </div>
          ))}
        </section>

        <LatestDataCard runs={syncRuns} syncing={syncingAll} />
      </main>
    </div>
  );
};

export const MarketingAppDashboard: React.FC<{ appId: MarketingAppId; section?: MarketingAppSection }> = ({
  appId,
  section = 'actions',
}) => {
  const [profile, setProfile] = useState<MarketingAppProfile | null>(null);
  const [syncRuns, setSyncRuns] = useState<MarketingSyncRun[]>([]);
  const [skillRuns, setSkillRuns] = useState<MarketingSkillRun[]>([]);
  const [postedVideos, setPostedVideos] = useState<MarketingPostedVideo[]>([]);
  const [analyticsSnapshot, setAnalyticsSnapshot] = useState<MarketingAnalyticsSnapshot | null>(null);
  const [selectedSkillId, setSelectedSkillId] = useState(MARKETING_SKILLS[0].id);
  const [objective, setObjective] = useState('');
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [syncingAnalytics, setSyncingAnalytics] = useState(false);
  const [running, setRunning] = useState(false);
  const [message, setMessage] = useState<string | null>(null);

  const selectedSkill = useMemo(
    () => MARKETING_SKILLS.find((skill) => skill.id === selectedSkillId) || MARKETING_SKILLS[0],
    [selectedSkillId],
  );

  const load = useCallback(async () => {
    setLoading(true);
    const [loadedProfile, loadedSyncs, loadedSkillRuns, loadedAnalytics, loadedPostedVideos] = await Promise.all([
      getMarketingProfile(appId),
      getRecentMarketingSyncRuns(appId),
      getMarketingSkillRuns(appId),
      getLatestMarketingAnalyticsSnapshot(appId),
      getMarketingPostedVideos(appId),
    ]);
    setProfile(loadedProfile);
    setSyncRuns(loadedSyncs);
    setSkillRuns(loadedSkillRuns);
    setAnalyticsSnapshot(loadedAnalytics);
    setPostedVideos(loadedPostedVideos);
    setLoading(false);
  }, [appId]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    setObjective(selectedSkill.defaultObjective);
  }, [selectedSkill.defaultObjective]);

  const runSync = async () => {
    setSyncing(true);
    setMessage(`Syncing ${profile?.name || appId} signals from Virlo and DataForSEO...`);
    try {
      const run = await requestMarketingSync(appId);
      const [updatedProfile, updatedSyncs, updatedSkillRuns] = await Promise.all([
        getMarketingProfile(appId),
        getRecentMarketingSyncRuns(appId),
        getMarketingSkillRuns(appId),
      ]);
      setProfile(updatedProfile);
      setSyncRuns([run, ...updatedSyncs.filter((item) => item.id !== run.id)]);
      setSkillRuns(updatedSkillRuns);
      setMessage(
        run.status === 'completed'
          ? 'Sync completed. New skill runs will use this latest data.'
          : 'Sync request saved. Refresh later for completion status.',
      );
    } finally {
      setSyncing(false);
    }
  };

  const runSkill = async () => {
    setRunning(true);
    setMessage(`Running ${selectedSkill.label}...`);
    const run = await runMarketingSkill(appId, selectedSkill.id, objective);
    setSkillRuns((prev) => [run, ...prev]);
    setMessage(`${selectedSkill.label} recommendation saved to marketing memory.`);
    setRunning(false);
  };

  const runAnalyticsSync = async () => {
    setSyncingAnalytics(true);
    setMessage(`Syncing ${profile?.name || appId} analytics from GA4/Firebase...`);
    try {
      const snapshots = await requestMarketingAnalyticsSync(appId);
      const snapshot = snapshots.find((item) => item.appId === appId) || snapshots[0] || await getLatestMarketingAnalyticsSnapshot(appId);
      setAnalyticsSnapshot(snapshot || null);
      setMessage(
        snapshot?.status === 'completed'
          ? 'Analytics sync completed. Campaign measurement is now using real app events.'
          : snapshot?.summary || 'Analytics sync needs setup.',
      );
    } finally {
      setSyncingAnalytics(false);
    }
  };

  const updateDecision = async (run: MarketingSkillRun, decision: MarketingRunDecision) => {
    await updateMarketingSkillDecision(run, decision);
    setSkillRuns((prev) => prev.map((item) => (item.id === run.id ? { ...item, decision } : item)));
  };

  const createPostedVideo = async (form: typeof emptyVideoForm) => {
    const hasInitialSnapshot = Boolean(form.views || form.likes || form.comments || form.shares || form.saves);
    const video = await createMarketingPostedVideo({
      appId,
      platform: form.platform,
      url: form.url,
      title: form.title,
      campaign: form.campaign,
      audio: form.audio,
      hashtags: parseHashtags(form.hashtags),
      snapshot: hasInitialSnapshot
        ? {
          views: Number(form.views || 0),
          likes: Number(form.likes || 0),
          comments: Number(form.comments || 0),
          shares: Number(form.shares || 0),
          saves: Number(form.saves || 0),
        }
        : undefined,
    });
    setPostedVideos((prev) => [video, ...prev]);
    setMessage('Posted video tracked. Add another snapshot after it has time to move.');
  };

  const addPostedVideoSnapshot = async (video: MarketingPostedVideo, form: typeof emptyVideoForm) => {
    const snapshot = await addMarketingPostedVideoSnapshot(video.id, {
      views: Number(form.views || 0),
      likes: Number(form.likes || 0),
      comments: Number(form.comments || 0),
      shares: Number(form.shares || 0),
      saves: Number(form.saves || 0),
    });
    setPostedVideos((prev) => prev.map((item) => (
      item.id === video.id ? { ...item, snapshots: [...item.snapshots, snapshot] } : item
    )));
    setMessage('Post metrics snapshot saved.');
  };

  if (loading || !profile) {
    return (
      <div className="marketing-dashboard">
        <Navigation />
        <main className="marketing-shell">
          <p className="marketing-empty">Loading marketing cockpit...</p>
        </main>
      </div>
    );
  }

  const viralTrendInputs = buildViralTrendInputs(syncRuns, profile);

  return (
    <div className="marketing-dashboard">
      <Navigation />
      <main className="marketing-shell">
        <header className={`marketing-hero marketing-hero--${appAccent(profile.id)}`}>
          <div>
            <span className="marketing-eyebrow">App workspace</span>
            <h1>{profile.name} Marketing</h1>
            <p>{profile.corePromise}</p>
          </div>
          <div className="marketing-actions">
            <button type="button" onClick={load} disabled={loading}>
              Refresh
            </button>
            <button type="button" className="marketing-primary" onClick={runSync} disabled={syncing}>
              {syncing ? 'Syncing...' : 'Run sync'}
            </button>
          </div>
        </header>

        <MarketingSubnav active={appId} />
        <MarketingAppSectionNav appId={appId} active={section} />
        {message && <div className="marketing-banner">{message}</div>}

        {section === 'actions' && (
          <>
            <WeeklyActionsPanel profile={profile} runs={skillRuns} analytics={analyticsSnapshot} trends={viralTrendInputs} />
            <TrendInputsPanel trends={viralTrendInputs} onSync={runSync} syncing={syncing} />
            <PostedVideosPanel
              videos={postedVideos}
              onCreate={createPostedVideo}
              onSnapshot={addPostedVideoSnapshot}
            />
            <section className="marketing-card marketing-execution-loop">
              <div className="marketing-card-header">
                <div>
                  <h3>Manual Execution Loop</h3>
                  <p className="marketing-muted">Keep this simple for the next 2-4 weeks.</p>
                </div>
              </div>
              <ol className="marketing-loop">
                <li>Choose up to 3 tests from this page.</li>
                <li>Post or launch them manually with UTMs where possible.</li>
                <li>Measure install to activation, not impressions alone.</li>
                <li>Mark the related recommendation executed only after real market contact.</li>
              </ol>
            </section>
          </>
        )}

        {section === 'evidence' && (
          <>
            <LatestDataCard runs={syncRuns} syncing={syncing} />
            <section className="marketing-two-column">
              <section className="marketing-card">
                <div className="marketing-card-header">
                  <h3>Brand Memory</h3>
                  <span className="marketing-count">{profile.primaryAudiences.length} audiences</span>
                </div>
                <p><strong>Market:</strong> {profile.market}</p>
                <p><strong>Promise:</strong> {profile.corePromise}</p>
                <div className="marketing-pill-list">
                  {profile.primaryAudiences.map((audience) => (
                    <span key={audience}>{audience}</span>
                  ))}
                </div>
                <h4>Proof</h4>
                <ul>
                  {profile.positioningProof.map((proof) => (
                    <li key={proof}>{proof}</li>
                  ))}
                </ul>
                <h4>Do not claim</h4>
                <ul>
                  {profile.rejectedClaims.map((claim) => (
                    <li key={claim}>{claim}</li>
                  ))}
                </ul>
              </section>

              <section className="marketing-card">
                <div className="marketing-card-header">
                  <h3>Weekly Operating Loop</h3>
                </div>
                <ol className="marketing-loop">
                  {profile.weeklyLoop.map((step) => (
                    <li key={step}>{step}</li>
                  ))}
                </ol>
              </section>
            </section>

            <section className="marketing-two-column">
              <SourceCard title="Virlo MCP/API" source={profile.virlo} />
              <SourceCard title="DataForSEO API" source={profile.dataForSeo} />
            </section>

            <section className="marketing-card">
              <div className="marketing-card-header">
                <h3>Measurement Events</h3>
                <span className="marketing-count">{profile.measurementEvents.length} events</span>
              </div>
              <div className="marketing-measurement-grid">
                {profile.measurementEvents.map((event) => (
                  <article key={event.id} className="marketing-measurement-card">
                    <h4>{event.label}</h4>
                    <p>{event.whyItMatters}</p>
                  </article>
                ))}
              </div>
            </section>
            <AnalyticsSnapshotCard
              snapshot={analyticsSnapshot}
              syncing={syncingAnalytics}
              onSync={runAnalyticsSync}
            />
            <SyncRuns runs={syncRuns} />
          </>
        )}

        {section === 'memory' && (
          <>
            <section className="marketing-card">
              <div className="marketing-card-header">
                <div>
                  <h3>Run Skill</h3>
                  <p className="marketing-muted">Outputs are based on {latestSyncLabel(syncRuns)}.</p>
                </div>
                <span className="marketing-count">{MARKETING_SKILLS.length} workflows</span>
              </div>
              <div className="marketing-run-skill">
                <label>
                  Skill workflow
                  <select value={selectedSkillId} onChange={(event) => setSelectedSkillId(event.target.value)}>
                    {MARKETING_SKILLS.map((skill) => (
                      <option key={skill.id} value={skill.id}>
                        {skill.label}
                      </option>
                    ))}
                  </select>
                </label>
                <label>
                  Objective
                  <textarea rows={3} value={objective} onChange={(event) => setObjective(event.target.value)} />
                </label>
                <div className="marketing-skill-preview">
                  <strong>{selectedSkill.sourceSkills.join(' + ')}</strong>
                  <p>{selectedSkill.description}</p>
                  <small>Output: {selectedSkill.outputTemplate.join(', ')}</small>
                </div>
                <button type="button" className="marketing-primary" onClick={runSkill} disabled={running}>
                  {running ? 'Running...' : 'Run skill'}
                </button>
              </div>
            </section>
            <SkillRuns runs={skillRuns} onDecision={updateDecision} />
          </>
        )}
      </main>
    </div>
  );
};

export default MarketingDashboard;
